import React from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import type { SettlementVerification, StatusCounts } from './use-finance-read-model.js';

const STATUS_COLORS: Record<string, string> = {
  verified: C.green,
  pending: C.gold,
  missing: C.red,
  variance: C.red,
};

const PERIOD_OPTIONS = [
  { value: 'last_month', label: 'Last month' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_week', label: 'This week' },
  { value: 'all', label: 'All' },
];

function formatCurrency(val: number | string | null | undefined): string {
  if (val == null || val === '') return '\u2014';
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (isNaN(num)) return '\u2014';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(val: string | null | undefined): string {
  if (!val) return '\u2014';
  const d = new Date(val);
  if (isNaN(d.getTime())) return val;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface FinanceReportListProps {
  verifications: SettlementVerification[];
  statusCounts: StatusCounts;
  loading: boolean;
  error: string | null;
  period: string;
  onPeriodChange: (period: string) => void;
  onRefresh: () => void;
}

export default function FinanceReportList({ verifications, statusCounts, loading, error, period, onPeriodChange, onRefresh }: FinanceReportListProps) {
  return (
    <div style={LAYOUT.subPanel}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textMuted }}>
          Settlement verification
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select
            value={period}
            onChange={(e) => onPeriodChange(e.target.value)}
            style={{
              background: C.surface,
              color: C.text,
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 6,
              padding: '4px 8px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {PERIOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <button
            onClick={onRefresh}
            style={{
              background: 'none',
              border: `1px solid ${C.borderSoft}`,
              borderRadius: 6,
              color: C.textMuted,
              padding: '4px 10px',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Status summary bar */}
      {statusCounts && statusCounts.total > 0 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 12 }}>
          <span style={{ color: C.text }}>{statusCounts.total} total</span>
          {statusCounts.verified > 0 && <span style={{ color: C.green }}>{statusCounts.verified} verified</span>}
          {statusCounts.pending > 0 && <span style={{ color: C.gold }}>{statusCounts.pending} pending</span>}
          {statusCounts.missing > 0 && <span style={{ color: C.red }}>{statusCounts.missing} missing</span>}
          {statusCounts.variance > 0 && <span style={{ color: C.red }}>{statusCounts.variance} variance</span>}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div style={{ color: C.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>
          Loading settlement data...
        </div>
      )}

      {/* Error state */}
      {error && !loading && (
        <div style={{ color: C.red, fontSize: 13, padding: 16, textAlign: 'center' }}>
          {error}
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && verifications.length === 0 && (
        <div style={{ color: C.textMuted, fontSize: 13, padding: 16, textAlign: 'center' }}>
          No settlement records for this period.
        </div>
      )}

      {/* Settlement list */}
      {!loading && !error && verifications.length > 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {verifications.map((row, i) => {
            const statusColor = STATUS_COLORS[(row.status || '').toLowerCase()] || C.textMuted;
            return (
              <div
                key={row.id ?? i}
                style={{
                  padding: 12,
                  borderRadius: 10,
                  background: C.panel,
                  border: `1px solid ${C.borderSoft}`,
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 8,
                  alignItems: 'start',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, color: C.text, marginBottom: 4 }}>
                    Week ending {formatDate(row.week_ending)}
                    {row.terminal ? ` \u00b7 ${row.terminal}` : ''}
                    {row.isp_number ? ` \u00b7 ISP ${row.isp_number}` : ''}
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <span>Settlement: {formatCurrency(row.settlement_amount)}</span>
                    <span>Deposit: {formatCurrency(row.deposit_amount)}</span>
                    {row.variance != null && row.variance !== 0 && (
                      <span style={{ color: C.red }}>Variance: {formatCurrency(row.variance)}</span>
                    )}
                    {row.lag_days != null && (
                      <span>Lag: {row.lag_days}d</span>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: '0.06em',
                    color: statusColor,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.status || 'unknown'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
